import { getPaymentDetails } from 'feature/payments/paymentSlice';
import useDevice from 'hooks/DesktopDevice.hook';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { Box, BoxOverflow, Button, Stack, Typography } from 'styles';
import { theme } from 'styles/theme/ThemeStyle';
import { useTheme } from 'stylesHooks';
import { PaymentDetailsType } from 'types/Payment';
import { PATHNAMES } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { formatDateWithoutZulu, formatPrice, getShortDate } from 'utils/utils';
import PaymentClaimsFooter from './PaymentClaims/PaymentClaimsFooter.component';
import PaymentClaimsHeader from './PaymentClaims/PaymentClaimsHeader.component';
import PaymentClaimsList from './PaymentClaims/PaymentClaimsList.component';

const TYPOGRAPHY_GAP = 1.25;

export const PriceBox: FC<{ price: string; isDesktop: boolean }> = ({
	price,
	isDesktop,
}) => {
	return (
		<Box
			bgcolor={theme.palette.grey5.main}
			px={1.875}
			borderRadius={'4px'}
			display="flex"
			height={isDesktop ? '24px' : '20px'}
		>
			<Typography
				variant={isDesktop ? 'value1' : 'bodyLink'}
				lineHeight={isDesktop ? '24px' : '20px'}
				margin="0 auto"
			>
				{price}
			</Typography>
		</Box>
	);
};

const Payment = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const param = useParams();
	const theme = useTheme();
	const { isDesktop } = useDevice();
	const dispatch = useDispatch<AppDispatch>();

	const { errorCode, errorMessage, paymentRun, paymentDetails } = useSelector(
		(store: RootState) => store.payment,
	);
	const { profile } = useSelector((store: RootState) => store.user);

	useEffect(() => {
		dispatch(
			getPaymentDetails({
				showloader: true,
				paymentRunId: param.id as string,
				detailType: PaymentDetailsType.EOP,
			}),
		);
	}, [dispatch, param.id]);

	if (errorCode !== 0) {
		return (
			<Typography variant="bodyLink">{`Payment ${param.id} ${errorMessage} for provider ${profile?.location}`}</Typography>
		);
	}

	const headerBoxSX = {
		display: 'flex',
		alignItems: 'center',
		width: '100% !important',
		gap: isDesktop ? 0 : theme.spacing(TYPOGRAPHY_GAP),
		justifyContent: 'space-between',
	};

	return (
		<>
			<BoxOverflow
				variant="y"
				sx={{
					marginTop: isDesktop ? 1.875 : 1.25,
				}}
			>
				<Box>
					<Stack direction={'row'} gap={isDesktop ? 5 : 2.5}>
						<Stack
							display={'flex'}
							flexDirection={'column'}
							justifyContent={'start'}
							alignSelf={'stretch'}
							padding={'20px'}
							bgcolor={theme.palette.white.main}
							borderRadius={'0px 0px 4px 4px'}
							width={'50%'}
							gap={0.625}
						>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.paymentRunId')}:
								</Typography>
								<Typography variant="bodyLink">
									{paymentDetails.paymentRunId}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.paymentRunDate')}:
								</Typography>
								<Typography variant="bodyLink">
									{getShortDate(paymentDetails.paymentRunDate)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX} mt={0.625}>
								<Typography variant="body">
									{tClientLabels('payment.labels.paymentRunAmount')}:
								</Typography>
								<PriceBox
									price={formatPrice(paymentDetails.total?.netDue)}
									isDesktop={isDesktop}
								/>
							</Box>
							<Box sx={headerBoxSX} my={0.625}>
								<Typography variant="bodyLink">
									{tClientLabels('payment.labels.totals')}:
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.retailAmount')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.totalCharges)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.memberPay')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.memberResponsible)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.planPay')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.claimPayment)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.tax')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.tax)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.retailNetOfTax')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.retailNetOfTax)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.eyemedFeeNetOfTax')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.assessmentFee)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.eyemedFeeTax')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.assessmentFeeTax)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('payment.labels.PSWT')}:
								</Typography>
								<Typography variant="bodyLink">
									{formatPrice(paymentDetails.total?.pswtAmount || 0)}
								</Typography>
							</Box>
							<Box sx={headerBoxSX} mt={0.625}>
								<Typography variant="body">
									{tClientLabels('payment.labels.netDue')}:
								</Typography>
								<PriceBox
									price={formatPrice(paymentDetails.total?.netDue)}
									isDesktop={isDesktop}
								/>
							</Box>
						</Stack>
						<Stack
							display={'flex'}
							flexDirection={'column'}
							justifyContent={'start'}
							alignSelf={'stretch'}
							bgcolor={theme.palette.white.main}
							borderRadius={'0px 0px 4px 4px'}
							width={'50%'}
							padding={'20px'}
							gap={0.625}
						>
							<Box sx={headerBoxSX} mb={0.75}>
								<Typography variant="bodyLink">
									{tClientLabels('payment.labels.eyemedFeeGroupInvoice')}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('paymeny.labels.invoiceNumber')}:
								</Typography>
								<Typography variant="bodyLink">
									{paymentDetails.paymentInfo.invoiceNumber}
								</Typography>
							</Box>
							<Box sx={headerBoxSX}>
								<Typography variant="body">
									{tClientLabels('paymeny.labels.invoiceDate')}:
								</Typography>
								<Typography variant="bodyLink">
									{getShortDate(paymentDetails.paymentInfo.invoiceDate)}
								</Typography>
							</Box>
							<Box
								display="flex"
								height="100%"
								alignItems="end"
								justifyContent="right"
							>
								<Link
									style={{
										textDecoration: 'none',
									}}
									to={`/${PATHNAMES.VIEW_PAYMENT_HISTORY}/${
										paymentRun.paymentRunId
									}|${formatDateWithoutZulu(paymentRun.paymentRunDate)}/${
										PATHNAMES.GROUP_INVOICE_ASSESSMENT
									}`}
								>
									<Button variant="linkDark">
										{tClientLabels('payment.labels.viewDetail')}
									</Button>
								</Link>
							</Box>
						</Stack>
					</Stack>
					<Box my={isDesktop ? 5 : 3.75}>
						<Typography variant="title5">
							{tClientLabels('payment.labels.relatedClaims')}
						</Typography>
					</Box>
					<Stack sx={{ overflowX: 'scroll' }}>
						<PaymentClaimsHeader />
						<PaymentClaimsList />
					</Stack>
				</Box>
				<PaymentClaimsFooter />
			</BoxOverflow>
		</>
	);
};

export default Payment;
